/**
 * Search over the page, opened with `/`.
 *
 * ## Why an overlay as well as a route
 *
 * The route is the right shape for the phone's tab bar, for a deep link and for the
 * television. It is the wrong shape for the case this covers: you are halfway down a
 * library, you want to check whether a film is in the catalogue, and searching costs you
 * the screen you were reading plus a Back press plus finding your place again. An overlay
 * costs a keypress and Escape.
 *
 * Both mount the same `SearchPanel`, so there is one search implementation, not two.
 *
 * ## Why not `Dialog`
 *
 * `Dialog` is the app's modal and its focus trap is the reference implementation, but it is
 * a *question* — it centres a box, titles it, and expects an answer. This is a surface: top
 * aligned, full height, its own scroll, and dismissed rather than answered. Reusing it
 * would mean overriding every one of those, which is the point where reuse stops saving
 * anything. The two behaviours worth borrowing — Escape closes, focus returns to where it
 * was — are eight lines and are below.
 *
 * ## Keyboard only, deliberately
 *
 * There is no button that opens this. `/` is a convention people either know or do not,
 * and someone who does not has the "Ara" item in the navigation, which goes to the route
 * and is not hidden behind a shortcut. Adding a second visible entry point would put two
 * differently-behaving search affordances in the same top bar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from './Icon';
import { SearchPanel } from './SearchPanel';

/**
 * True when a keystroke belongs to something the user is typing into.
 *
 * Without it, typing "/" into any text field on the page opens the overlay instead of
 * inserting a slash — including into the search field of the overlay itself.
 */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

export function SearchOverlay() {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const { pathname } = useLocation();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        close();
        return;
      }
      // No modifier: Ctrl+/ and Cmd+/ belong to the browser and to editors.
      if (e.key === '/' && !open && !e.ctrlKey && !e.metaKey && !e.altKey && !isTyping(e.target)) {
        e.preventDefault();
        opener.current = document.activeElement as HTMLElement | null;
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  /*
   * A navigation closes it.
   *
   * Opening a result is a navigation, and an overlay still sitting over the page you just
   * asked for is the single most confusing thing this component could do. Keyed on the
   * path rather than handled per-link, so it also covers the browser's own Back and any
   * route change the panel does not know about.
   */
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  /*
   * The page underneath does not scroll while this is open.
   *
   * Without it, a wheel gesture past the end of the overlay's own scroll keeps going into
   * the page behind — which then has to be scrolled back after closing.
   */
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  /* Focus goes back where it came from, the way `Dialog` does it. */
  useEffect(() => {
    if (open) return;
    const target = opener.current;
    opener.current = null;
    if (target && document.body.contains(target)) target.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="searchoverlay"
      role="dialog"
      aria-modal="true"
      aria-label="Ara"
      // Only a click on the backdrop itself, never one that bubbled up from the box.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <button type="button" className="searchoverlay__close" onClick={close} aria-label="Kapat">
        <Icon name="close" size={1.2} />
      </button>
      <div className="searchoverlay__box">
        {/* The panel's own `autoFocus` puts the caret in the field on open, which is the
            entire point of a keyboard shortcut. */}
        <SearchPanel onNavigate={close} />
      </div>
    </div>
  );
}
